import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Badge, List, ListItem, ListIcon, Button, HStack } from "@chakra-ui/react";
import { FaCheckCircle, FaSolarPanel, FaHome } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SolarServicesResidential: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/services/residential"
        fallbackTitle="Residential Solar Services"
        fallbackDescription="Custom solar solutions designed for homes and residential properties"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        position="relative"
        color="white"
        py={{ base: 16, md: 20 }}
        backgroundImage="url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Heading size="2xl" fontWeight="bold">
              Residential Solar Systems
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              Premium home solar packages designed for Australian households
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* System Size Options */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Heading size="xl" textAlign="center">Choose Your System Size</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="2xl">
            Select the perfect solar system based on your household energy needs
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          {/* Small System */}
          <Box
            borderWidth={2}
            borderColor="gray.200"
            borderRadius="xl"
            overflow="hidden"
            _hover={{ shadow: "xl", transform: "translateY(-4px)", borderColor: "#C69214" }}
            transition="all 0.3s"
          >
            <Box bg="gray.50" p={6} textAlign="center">
              <Badge bg="#C69214" color="white" fontSize="sm" mb={2}>STARTER</Badge>
              <Heading size="lg">5-6.6 kW</Heading>
              <Text color="gray.600" mt={2}>Small Households</Text>
            </Box>
            <VStack p={6} spacing={4} align="stretch">
              <Text fontWeight="bold" color="gray.700">Ideal For:</Text>
              <List spacing={2}>
                <ListItem fontSize="sm">
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  1-2 bedroom homes
                </ListItem>
                <ListItem fontSize="sm">
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Daily usage 10-15 kWh
                </ListItem>
                <ListItem fontSize="sm">
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Couples or small families
                </ListItem>
              </List>
              <Box pt={4}>
                <Text fontSize="2xl" fontWeight="bold" color="#141C49">
                  From $4,500
                </Text>
                <Text fontSize="sm" color="gray.500">After rebates</Text>
              </Box>
              <Button bg="#141C49" color="white" size="lg" _hover={{ bg: "#a07e10" }} transition="all 0.2s">Get Quote</Button>
            </VStack>
          </Box>

          {/* Medium System */}
          <Box
            borderWidth={3}
            borderColor="#C69214"
            borderRadius="xl"
            overflow="hidden"
            position="relative"
            _hover={{ shadow: "2xl", transform: "translateY(-4px)" }}
            transition="all 0.3s"
          >
            <Badge
              position="absolute"
              top={4}
              right={4}
              bg="#C69214"
              color="white"
              fontSize="xs"
              px={3}
              py={1}
            >
              MOST POPULAR
            </Badge>
            <Box bg="gray.50" p={6} textAlign="center">
              <Badge bg="#141C49" color="white" fontSize="sm" mb={2}>STANDARD</Badge>
              <Heading size="lg">8-10 kW</Heading>
              <Text color="gray.600" mt={2}>Medium Households</Text>
            </Box>
            <VStack p={6} spacing={4} align="stretch">
              <Text fontWeight="bold" color="gray.700">Ideal For:</Text>
              <List spacing={2}>
                <ListItem fontSize="sm">
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  3-4 bedroom homes
                </ListItem>
                <ListItem fontSize="sm">
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Daily usage 20-30 kWh
                </ListItem>
                <ListItem fontSize="sm">
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Families with pool/AC
                </ListItem>
              </List>
              <Box pt={4}>
                <Text fontSize="2xl" fontWeight="bold" color="#141C49">
                  From $6,800
                </Text>
                <Text fontSize="sm" color="gray.500">After rebates</Text>
              </Box>
              <Button bg="#141C49" color="white" size="lg" _hover={{ bg: "#a07e10" }} transition="all 0.2s">Get Quote</Button>
            </VStack>
          </Box>

          {/* Large System */}
          <Box
            borderWidth={2}
            borderColor="gray.200"
            borderRadius="xl"
            overflow="hidden"
            _hover={{ shadow: "xl", transform: "translateY(-4px)", borderColor: "#C69214" }}
            transition="all 0.3s"
          >
            <Box bg="gray.50" p={6} textAlign="center">
              <Badge bg="#C69214" color="white" fontSize="sm" mb={2}>PREMIUM</Badge>
              <Heading size="lg">13+ kW</Heading>
              <Text color="gray.600" mt={2}>Large Households</Text>
            </Box>
            <VStack p={6} spacing={4} align="stretch">
              <Text fontWeight="bold" color="gray.700">Ideal For:</Text>
              <List spacing={2}>
                <ListItem fontSize="sm">
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  4+ bedroom homes
                </ListItem>
                <ListItem fontSize="sm">
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Daily usage 30+ kWh
                </ListItem>
                <ListItem fontSize="sm">
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Large families/home offices
                </ListItem>
              </List>
              <Box pt={4}>
                <Text fontSize="2xl" fontWeight="bold" color="#141C49">
                  From $9,500
                </Text>
                <Text fontSize="sm" color="gray.500">After rebates</Text>
              </Box>
              <Button bg="#141C49" color="white" size="lg" _hover={{ bg: "#a07e10" }} transition="all 0.2s">Get Quote</Button>
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Features Checklist */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8} mb={12}>
            <Heading size="xl" textAlign="center">What's Included</Heading>
            <Text fontSize="lg" color="gray.600" textAlign="center" maxW="2xl">
              Every residential system includes premium components and professional service
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            <Box bg="white" p={8} borderRadius="lg" shadow="md">
              <Heading size="md" mb={6} color="#141C49">
                Premium Equipment
              </Heading>
              <List spacing={3}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Tier 1 monocrystalline solar panels
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Premium inverter (Fronius, SolarEdge, or Enphase)
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Australian-made racking system
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Smart monitoring system
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Bird-proofing mesh (if required)
                </ListItem>
              </List>
            </Box>

            <Box bg="white" p={8} borderRadius="lg" shadow="md">
              <Heading size="md" mb={6} color="#141C49">
                Professional Service
              </Heading>
              <List spacing={3}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Free site assessment and quote
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  CEC accredited installation team
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  Grid connection paperwork handled
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  25-year performance warranty
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" />
                  10-year workmanship guarantee
                </ListItem>
              </List>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA */}
      <Container maxW="container.xl" py={16}>
        <Box
          bg="gray.800"
          p={12}
          borderRadius="2xl"
          textAlign="center"
          color="white"
        >
          <HStack justify="center" mb={4} color="#C69214">
            <FaSolarPanel size={40} />
            <FaHome size={40} />
          </HStack>
          <Heading size="xl" mb={4}>Ready to Power Your Home?</Heading>
          <Text fontSize="lg" mb={6} color="gray.300">
            Get a free site assessment and custom quote for your home
          </Text>
          <Button size="lg" bg="white" color="#141C49" _hover={{ bg: "#a07e10", color: "white" }} transition="all 0.2s">
            Schedule Free Assessment
          </Button>
        </Box>
      </Container>

      <SolarFooter />
    </Box>
  );
};

export default SolarServicesResidential;
